import { FC, ReactElement, useMemo } from 'react';
import { ComposableTable, SingleSelectDropdown, Tooltip } from '@abtasty/design-system';
import { QATest, QaTestType } from '@src/application/modules/tag/shared/tag/types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSegmentTrack } from '@application/hooks/useSegment/useSegment';
import { TestIcon } from '../TestIcon/TestIcon';
import { useNavigate } from 'react-router-dom';
import { changeVariation } from '../../helpers';
import styles from '@application/modules/shared/styles/CampaignSummary/CampaignSummary.module.scss';
import {
  TestStatus,
  TestStatusAccepted,
  TestStatusForced,
  TestStatusRejected,
  TestStatusWaiting,
  TestSubType,
} from '@src/application/modules/tag/shared/tag/serializedTagTypes';
import { DivWithTooltipOnClamped } from '@src/application/modules/shared/components/DivWithTooltipOnClamped/DivWithTooltipOnClamped';
import { StyledCell, StyledCellTwoLines } from './styledComponents';
import { CampaignStatusIcon, CampaignStatusTag } from './CampaignStatus';
import { ExpandIcon } from '@src/application/modules/shared/components/ExpandIcon';
import { CampaignColumn, ColumnHeaders } from '@src/application/modules/shared/type';
import { CampaignDisplayButton } from './CampaignDisplayButton';
import { isStatusForced } from '../../../shared/tag/helpers';
import { ExclusionGroupDropdown } from './components/ExclusionGroupDropdown';
import { useIsMobile } from '@src/application/hooks/useIsMobile';

const { Row } = ComposableTable;

type CampaignSummaryProps = {
  test: QATest;
  expand: () => void;
  expanded: boolean;
  header: ColumnHeaders<CampaignColumn>;
};

type CampaignSummaryRowProps = { large: boolean; children: ReactElement };
function CampaignSummaryRow({ large, children }: CampaignSummaryRowProps) {
  return (
    <Row className={classNames(styles.row, { [styles.largeRow]: large })} dataTestId="row-campaign">
      {children}
    </Row>
  );
}

export const CampaignSummary: FC<CampaignSummaryProps> = ({ test, expand, expanded, header }) => {
  const testID = test.id;
  const { t } = useTranslation();
  const segmentTrack = useSegmentTrack();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const mobileDetailsLink = () => navigate(`campaigns/${test.id}/details`);

  const furthestCheck = (status: TestStatus, step: number) => {
    if (isStatusForced(status)) {
      return '-';
    }

    switch (status) {
      case TestStatusRejected.targetPagesRejected:
      case TestStatusWaiting.waitingCodeResolution:
        return '-';
      case TestStatusRejected.displayFrequency:
        if (step > 2) return '-';
        if (step === 2) return t('Rejected');
        return t('Accepted');
      case TestStatusRejected.triggerRejected:
        if (step > 3) return '-';
        if (step === 3) return t('Rejected');
        return t('Accepted');
      case TestStatusRejected.segmentRejected:
        if (step === 4) return t('Rejected');
        return t('Accepted');
      case TestStatusWaiting.pending:
        return t('pending');
      case TestStatusWaiting.targetByEventPending:
        return t('Event Targeting');
      case TestStatusRejected.notPrioritizedYet:
        return t('Not prioritized yet');
      default:
        return t('Accepted');
    }
  };

  const secondaryTitle =
    t('ID: {{testId}}', { testId: test.id }) +
    (test.parent
      ? ` - ${t('Master: [{{masterTestId}}] {{masterTestName}}', {
          masterTestId: test.parent.id,
          masterTestName: test.parent.name,
        })}`
      : '');

  const isLargeRow = !!test.exclusionGroup;
  const hasDetails = isStatusForced(test.status) ? false : true;

  const handleExpandRow = useMemo(() => {
    return hasDetails ? expand : undefined;
  }, [expand, hasDetails]);

  if (isMobile) {
    return (
      <CampaignSummaryRow large={isLargeRow}>
        <>
          <StyledCell onClick={mobileDetailsLink} width={header.type.width} className={styles.cell}>
            <TestIcon testType={test.type} testSubType={test.parent?.subType} />
          </StyledCell>
          {test.exclusionGroup ? (
            <StyledCell
              onClick={mobileDetailsLink}
              className={classNames(styles.cell, styles.column)}
              width={header.name.width}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <ExclusionGroupDropdown test={test} />
              </div>
              <DivWithTooltipOnClamped className={styles.secondary}>{secondaryTitle}</DivWithTooltipOnClamped>
            </StyledCell>
          ) : (
            <StyledCellTwoLines
              onClick={mobileDetailsLink}
              width={header.name.width}
              text={test.name}
              secondaryText={secondaryTitle}
            />
          )}
          <StyledCell onClick={mobileDetailsLink} width={header.qaMode.width} dataTestId="test-status" align="center">
            <CampaignStatusIcon status={test.status} />
          </StyledCell>
        </>
      </CampaignSummaryRow>
    );
  }

  const isPerso = test.parent?.subType;
  const isRedirectedVariation = !!(test.status === TestStatusAccepted.acceptedByRedirection && test.activeVariation);

  const isSimplePerso = test.parent?.subType === TestSubType.simplePersonalization;

  return (
    <CampaignSummaryRow large={isLargeRow}>
      <>
        {/* Test Type */}
        <StyledCell onClick={handleExpandRow} width={header.type.width} className={styles.cell}>
          <TestIcon testType={test.type} testSubType={test.parent?.subType || test.testDatas.sub_type} />
        </StyledCell>

        {/* Test Name */}
        {test.exclusionGroup ? (
          <StyledCell className={classNames(styles.cell, styles.column)} width={header.name.width}>
            <ExclusionGroupDropdown test={test} />
            <DivWithTooltipOnClamped className={styles.secondary}>{secondaryTitle}</DivWithTooltipOnClamped>
          </StyledCell>
        ) : (
          <StyledCellTwoLines
            onClick={handleExpandRow}
            width={header.name.width}
            text={isSimplePerso ? test.parent!.name : test.name}
            secondaryText={secondaryTitle}
          />
        )}

        {/* Variation/Experience */}
        {test.status !== TestStatusForced.forceRejected && // TODO: challenge once mock is removed
        test.activeVariation &&
        test.variationsData ? (
          <StyledCell width={header.variation.width}>
            <Tooltip
              targetClassName="tooltip"
              type="small"
              placement="top"
              content={
                isRedirectedVariation &&
                'You’re affected on a Split test on a redirected URL, so you can’t switch back to original variation.'
              }
            >
              <SingleSelectDropdown
                className="single-select-dropdown"
                componentDataTestIds={{ ValueContainer: 'variationsDropdownValue' }}
                isDisabled={isRedirectedVariation || test.type === QaTestType.aa}
                selectorType="outlined32"
                onChange={({ value }) => {
                  if (test.activeVariation) {
                    changeVariation(testID, test.activeVariation.id, value, test.status);
                  }
                  segmentTrack('Qa - Click variations dropdown');
                }}
                value={{
                  value: test.activeVariation.id,
                  label: isPerso && test.activeVariation.id !== 0 ? test.name : test.activeVariation.name,
                }}
                options={[
                  {
                    label: t('Original'),
                    value: 0,
                  },
                  ...test.variationsData.map(({ name, id }) => ({
                    label: isPerso && id !== 0 ? test.name : name,
                    value: id,
                  })),
                ]}
              />
            </Tooltip>
          </StyledCell>
        ) : (
          <StyledCell
            onClick={handleExpandRow}
            width={header.variation.width}
            className={classNames(styles.select, styles.null, styles.smallValue)}
          >
            {test.status === TestStatusForced.forceRejected ? t('Campaign hidden') : t('Not affected to test')}
          </StyledCell>
        )}

        {/* QA Mode */}
        <StyledCell
          onClick={handleExpandRow}
          width={header.qaMode.width}
          dataTestId="qa-mode"
          className={styles.smallValue}
        >
          {(test.QAModeActive ? t('In QA') : t('Live')) as string}
        </StyledCell>

        {/* Display Frequency */}
        <StyledCell
          onClick={handleExpandRow}
          width={header.displayFrequency.width}
          className={classNames(
            { [styles.rejected]: test.status === TestStatusRejected.displayFrequency },
            styles.smallValue
          )}
          dataTestId="displayFrequency"
        >
          {furthestCheck(test.status, 2)}
        </StyledCell>

        {/* Trigger */}
        <StyledCell
          onClick={handleExpandRow}
          width={header.trigger.width}
          className={classNames(
            { [styles.rejected]: test.status === TestStatusRejected.triggerRejected },
            styles.smallValue
          )}
          dataTestId="trigger"
        >
          {furthestCheck(test.status, 3)}
        </StyledCell>

        {/* Segment */}
        <StyledCell
          onClick={handleExpandRow}
          width={header.segment.width}
          className={classNames(
            { [styles.rejected]: test.status === TestStatusRejected.segmentRejected },
            styles.smallValue
          )}
          dataTestId="segment"
        >
          {furthestCheck(test.status, 4)}
        </StyledCell>

        {/* Test Status */}
        <StyledCell onClick={handleExpandRow} width={header.status.width} dataTestId="test-status">
          <CampaignStatusTag status={test.status} />
        </StyledCell>

        {
          /* Campaign display */
          header.campaignDisplay && (
            <StyledCell width={header.campaignDisplay.width}>
              <CampaignDisplayButton id={test.id} status={test.status} />
            </StyledCell>
          )
        }

        {/* Expand Chevron */}
        {handleExpandRow && (
          <StyledCell onClick={handleExpandRow} width={header.expand.width} align="right">
            <ExpandIcon expanded={expanded} />
          </StyledCell>
        )}
      </>
    </CampaignSummaryRow>
  );
};

export default CampaignSummary;
